import type { ReactNode } from 'react';
import React, { useEffect, useState } from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';

import {
  fetchUser,
  fetchUserPaymentsIfNeeded,
  recoverRefreshTokenIfNeeded,
} from 'actions';
import { Spinner } from 'components/Spinner/Spinner';
import type { RootState } from 'reducers/types';
import { isLoggedIn } from 'selectors';

type AuthUserProps = ConnectedProps<typeof connector> & {
  children?: ReactNode | undefined;
};

function AuthUserComponent({
  recoverRefreshTokenIfNeeded,
  children,
  fetchUser,
  fetchUserPaymentsIfNeeded,
  isLoggedIn,
}: AuthUserProps) {
  const [isFetching, setIsFetching] = useState(true);

  function stopFetching() {
    setIsFetching(false);
  }

  useEffect(() => {
    async function fetchData() {
      if (isLoggedIn) {
        await fetchUser();
        await fetchUserPaymentsIfNeeded();
      }

      stopFetching();
    }

    fetchData();

    return () => stopFetching();
  }, [fetchUser, fetchUserPaymentsIfNeeded, isLoggedIn]);

  useEffect(() => {
    window.addEventListener('focus', recoverRefreshTokenIfNeeded);

    return () => {
      window.removeEventListener('focus', recoverRefreshTokenIfNeeded);
    };
  }, [recoverRefreshTokenIfNeeded]);

  if (isFetching) {
    return <Spinner />;
  }

  return <>{children}</>;
}

const mapStateToProps = (state: RootState) => ({
  isLoggedIn: isLoggedIn(state),
});

const mapDispatchToProps = {
  recoverRefreshTokenIfNeeded: recoverRefreshTokenIfNeeded,
  fetchUser,
  fetchUserPaymentsIfNeeded,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
const AuthUser = connector(AuthUserComponent);

export { AuthUser };
